import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { WeldNdtMethodConfig, WeldNdtReportData } from '@/types/weldNdtTypes'
import { drawMethodTitle, getNdtResultBodyStyles, getNdtResultTableOptions } from './pdfTitle'
import {
  addNdtFooter,
  formatDateDDMMYYYY,
  getCertificateNumber,
  getFullName,
  getLogoDataUrl,
  getSignature,
  NDT_TABLE_OPTIONS,
} from './ndtPdfShared'

const dash = (value?: string | null) => {
  const trimmed = (value || '').trim()
  return trimmed || '---'
}

const formatExtentOfExamination = (extent: string) => {
  if (extent === 'Acc. to spec' || Number.isNaN(Number(extent))) return extent
  return `${extent}%`
}

export async function generateWeldNdtPDF(
  data: WeldNdtReportData,
  config: WeldNdtMethodConfig,
): Promise<Blob> {
  const verifierName = (data.verifiedBy || '').trim()

  const [inspectorSignature, verifierSignature, logoDataUrl, certificationNumber] = await Promise.all([
    getSignature(data.issuedBy),
    verifierName ? getSignature(verifierName) : Promise.resolve(null),
    getLogoDataUrl(),
    getCertificateNumber(data.issuedBy),
  ])

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const pageWidth = doc.internal.pageSize.width
  const pageHeight = doc.internal.pageSize.height
  const margin = 15

  drawMethodTitle(doc, config.methodTitle, margin, margin)
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', pageWidth - margin - 40, margin - 5, 40, 12)
  }

  let yPos = margin + 15
  const tableOptions = { ...NDT_TABLE_OPTIONS, margin: { left: margin, right: margin } }
  const leftColumnWidth = pageWidth * 0.7 - margin
  const rightColumnWidth = pageWidth * 0.3 - margin
  const lastY = () => (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY

  autoTable(doc, {
    startY: yPos,
    head: [['TEST DATE', 'LOCATION', 'REPORT NO.']],
    body: [[
      formatDateDDMMYYYY(data.testDate),
      data.testSite || 'Bergen',
      `${config.method}-${data.reportNumber ?? 'N/A'}.${data.sequenceNumber || '001'}`,
    ]],
    ...tableOptions,
    columnStyles: {
      0: { cellWidth: leftColumnWidth / 2 },
      1: { cellWidth: leftColumnWidth / 2 },
      2: { cellWidth: rightColumnWidth, overflow: 'linebreak', halign: 'left', valign: 'middle' },
    },
  })
  yPos = lastY()

  autoTable(doc, {
    startY: yPos,
    head: [['OBJECT', 'MATERIAL']],
    body: [[dash(data.objectDescription), dash(data.material)]],
    ...tableOptions,
    columnStyles: { 0: { cellWidth: leftColumnWidth }, 1: { cellWidth: rightColumnWidth } },
  })
  yPos = lastY()

  autoTable(doc, {
    startY: yPos,
    head: [['DRAWING', 'DIAMETER', 'THICKNESS']],
    body: [[dash(data.drawing), dash(data.diameter), dash(data.thickness)]],
    ...tableOptions,
    columnStyles: {
      0: { cellWidth: (pageWidth - 2 * margin) - rightColumnWidth },
      1: { cellWidth: rightColumnWidth / 2 },
      2: { cellWidth: rightColumnWidth / 2 },
    },
  })
  yPos = lastY()

  autoTable(doc, {
    startY: yPos,
    head: [['WELDER', 'WELDING PROCEDURE']],
    body: [[dash(data.welder), dash(data.weldingProcedure)]],
    ...tableOptions,
    columnStyles: { 0: { cellWidth: leftColumnWidth }, 1: { cellWidth: rightColumnWidth } },
  })
  yPos = lastY()

  autoTable(doc, {
    startY: yPos,
    head: [['JOINT', 'PWHT', 'FILLER MET', 'WELDING METHOD']],
    body: [[dash(data.joint), dash(data.pwht), dash(data.fillerMet), dash(data.weldingMethod)]],
    ...tableOptions,
    columnStyles: {
      0: { cellWidth: leftColumnWidth / 3 },
      1: { cellWidth: leftColumnWidth / 3 },
      2: { cellWidth: leftColumnWidth / 3 },
      3: { cellWidth: rightColumnWidth },
    },
  })
  yPos = lastY()

  const certNumber = `STMI / ${certificationNumber || '---'}${config.certSuffix}`
  autoTable(doc, {
    startY: yPos,
    head: [['SURFACE CONDITIONS', 'TEMPERATURE OF OBJECT', 'COMPANY / OPERATOR CERT NO']],
    body: [[
      dash(data.surfaceCondition || 'Cleaned'),
      dash(data.temperatureOfObject || '0-35°C'),
      certNumber,
    ]],
    ...tableOptions,
    columnStyles: {
      0: { cellWidth: leftColumnWidth / 2, overflow: 'linebreak' },
      1: { cellWidth: leftColumnWidth / 2, overflow: 'linebreak' },
      2: { cellWidth: rightColumnWidth, overflow: 'linebreak' },
    },
  })
  yPos = lastY()

  autoTable(doc, {
    startY: yPos,
    head: [['TEST PROCEDURE']],
    body: [[dash(data.testProcedure || config.defaultProcedure)]],
    ...tableOptions,
  })
  yPos = lastY()

  autoTable(doc, {
    startY: yPos,
    head: [['ACCEPTANCE', 'EXTENT OF EXAMINATION']],
    body: [[
      dash(data.testAcceptance || config.defaultAcceptance),
      formatExtentOfExamination(data.extentOfExamination),
    ]],
    ...tableOptions,
    columnStyles: { 0: { cellWidth: leftColumnWidth }, 1: { cellWidth: rightColumnWidth } },
  })
  yPos = lastY()

  autoTable(doc, {
    startY: yPos,
    head: [['REFERENCE TO APPLICABLE RULES AND STANDARDS']],
    body: [[dash(data.referenceStandards || config.defaultReference)]],
    ...tableOptions,
  })
  yPos = lastY()

  const objectsList =
    data.objects?.some((obj) => obj.trim())
      ? data.objects.filter((obj) => obj.trim()).map((obj) => `- ${obj}`).join('\n')
      : `- ${dash(data.objectDescription)}`

  const remainingSpace = pageHeight - 2 * margin - yPos - 55
  autoTable(doc, {
    startY: yPos,
    head: [['SUMMARY']],
    body: [[
      `STM has performed ${config.method} of following ID(s):\n\n` +
      objectsList +
      (data.comments?.trim() ? `\n\nCOMMENTS:\n\n${data.comments.trim()}` : ''),
    ]],
    ...tableOptions,
    bodyStyles: { fontSize: 8, minCellHeight: Math.max(remainingSpace, 28) },
  })
  yPos = lastY()

  autoTable(doc, {
    startY: yPos,
    head: [['INDICATIONS AND/OR NUMBER OF REPAIRS']],
    body: [[data.indications?.trim() ? data.indications.trim() : 'None']],
    ...tableOptions,
  })
  yPos = lastY()

  const isRejected = data.status === 'Not Accepted'
  autoTable(doc, {
    startY: yPos,
    head: [['RESULT']],
    body: [[isRejected ? 'REJECTED' : 'ACCEPTED']],
    ...getNdtResultTableOptions(margin),
    bodyStyles: getNdtResultBodyStyles(isRejected),
  })
  yPos = lastY()

  const signatureWidth = (pageWidth - 2 * margin) / 2
  const hasVerifier = !!verifierName
  doc.setDrawColor(0)
  doc.setLineWidth(0.1)

  if (hasVerifier) {
    doc.rect(margin, yPos, signatureWidth, 25)
    doc.rect(margin + signatureWidth, yPos, signatureWidth, 25)
    doc.setFontSize(7)
    doc.setFont('helvetica', 'bold')
    doc.text('ISSUED BY', margin + signatureWidth / 2, yPos + 4, { align: 'center' })
    doc.text('VERIFIED BY', margin + signatureWidth + signatureWidth / 2, yPos + 4, { align: 'center' })
  } else {
    doc.rect(margin, yPos, pageWidth - 2 * margin, 25)
    doc.setFontSize(7)
    doc.setFont('helvetica', 'bold')
    doc.text('ISSUED BY', margin + (pageWidth - 2 * margin) / 2, yPos + 4, { align: 'center' })
  }

  if (inspectorSignature) {
    doc.addImage(
      inspectorSignature,
      'PNG',
      hasVerifier ? margin + signatureWidth / 2 - 20 : pageWidth / 2 - 20,
      yPos + 8,
      40,
      10,
    )
  }
  if (hasVerifier && verifierSignature) {
    doc.addImage(verifierSignature, 'PNG', margin + signatureWidth + signatureWidth / 2 - 20, yPos + 8, 40, 10)
  }

  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  const signatureSite = data.testSite || 'Bergen'
  if (hasVerifier) {
    doc.text(`${getFullName(data.issuedBy)} / ${formatDateDDMMYYYY(data.testDate)} / ${signatureSite}`, margin + signatureWidth / 2, yPos + 22, { align: 'center' })
    doc.text(`${getFullName(verifierName)} / ${formatDateDDMMYYYY(data.testDate)} / ${signatureSite}`, margin + signatureWidth + signatureWidth / 2, yPos + 22, { align: 'center' })
  } else {
    doc.text(`${getFullName(data.issuedBy)} / ${formatDateDDMMYYYY(data.testDate)} / ${signatureSite}`, pageWidth / 2, yPos + 22, { align: 'center' })
  }

  if (data.attachedImage) {
    doc.addPage()
    drawMethodTitle(doc, config.methodTitle, margin, margin, { suffix: ' - ATTACHED IMAGE' })
    if (logoDataUrl) doc.addImage(logoDataUrl, 'PNG', pageWidth - margin - 40, margin - 5, 40, 12)
    doc.setDrawColor(0)
    doc.setLineWidth(0.5)
    doc.rect(margin, margin + 10, pageWidth - 2 * margin, pageHeight - 2 * margin - 10)
    try {
      const availableWidth = pageWidth - 4 * margin
      const availableHeight = pageHeight - 4 * margin - 20
      doc.addImage(
        data.attachedImage,
        'JPEG',
        margin + (availableWidth - availableWidth * 0.8) / 2 + margin,
        margin + 20 + (availableHeight - availableHeight * 0.8) / 2 + margin,
        availableWidth * 0.8,
        availableHeight * 0.8,
      )
    } catch {
      doc.setFontSize(12)
      doc.text('Error loading attached image', pageWidth / 2, pageHeight / 2, { align: 'center' })
    }
  }

  addNdtFooter(doc)
  return doc.output('blob')
}
